import { Capacitor } from "@capacitor/core";
import { AccessControl, NativeBiometric } from "@capgo/capacitor-native-biometric";

import type { PrivacySecurityState } from "./Models/SecurityState";
import { LocalStorageNamespace } from "@/Modules/App/Models/Localstorage/LocalStorageNamespace";
import { LocalStorageKey } from "@/Modules/App/Models/Localstorage/LocalStorageKey";

import { LocalStorageService } from "@/Services/LocalStorageService";

import { isValidString } from "@/Utils/String";

const privacyStorage = new LocalStorageService(LocalStorageNamespace.PRIVACY);
const SECURITY_STATE_VERSION = 1;

const isNonNegativeInteger = (value: unknown): value is number => {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
};

const parseSecurityState = (value: unknown): PrivacySecurityState => {
  const parsedValue = typeof value === "string" ? JSON.parse(value) : value;

  if (typeof parsedValue !== "object" || parsedValue === null) {
    throw new Error("Invalid privacy security state");
  }

  const state = parsedValue as Partial<PrivacySecurityState>;

  if (state.version !== SECURITY_STATE_VERSION || !isValidString(state.pinHash)) {
    throw new Error("Invalid privacy security state");
  }

  if (!isNonNegativeInteger(state.pinValidUntil)) {
    throw new Error("Invalid privacy security state");
  }

  if (!isNonNegativeInteger(state.failedPinAttempts)) {
    throw new Error("Invalid privacy security state");
  }

  if (!isNonNegativeInteger(state.pinLockLevel)) {
    throw new Error("Invalid privacy security state");
  }

  if (!isNonNegativeInteger(state.pinLockUntil)) {
    throw new Error("Invalid privacy security state");
  }

  return state as PrivacySecurityState;
};

export const getPrivacySecurityState = async (): Promise<PrivacySecurityState | undefined> => {
  if (!Capacitor.isNativePlatform()) {
    const storedState = privacyStorage.get<unknown>(LocalStorageKey.PRIVACY_SECURITY_STATE);

    return storedState === undefined || storedState === null ? undefined : parseSecurityState(storedState);
  }

  const { isSaved } = await NativeBiometric.isDataSaved({
    key: LocalStorageKey.PRIVACY_SECURITY_STATE,
  });

  if (!isSaved) {
    return undefined;
  }

  const { value } = await NativeBiometric.getData({
    key: LocalStorageKey.PRIVACY_SECURITY_STATE,
  });

  return parseSecurityState(value);
};

export const setPrivacySecurityState = async (state: PrivacySecurityState): Promise<void> => {
  const value = JSON.stringify(state);

  if (!Capacitor.isNativePlatform()) {
    privacyStorage.set(LocalStorageKey.PRIVACY_SECURITY_STATE, value);

    return;
  }

  await NativeBiometric.setData({
    key: LocalStorageKey.PRIVACY_SECURITY_STATE,
    value,
    accessControl: AccessControl.NONE,
  });
};

export const deletePrivacySecurityState = async (): Promise<void> => {
  if (!Capacitor.isNativePlatform()) {
    privacyStorage.delete(LocalStorageKey.PRIVACY_SECURITY_STATE);

    return;
  }

  const { isSaved } = await NativeBiometric.isDataSaved({
    key: LocalStorageKey.PRIVACY_SECURITY_STATE,
  });

  if (isSaved) {
    await NativeBiometric.deleteData({
      key: LocalStorageKey.PRIVACY_SECURITY_STATE,
    });
  }
};
